import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Users, Briefcase, UserPlus, ClipboardCheck, FileCheck, Award,
  ChevronRight, Plus, Eye, ArrowLeft, Calendar, CheckCircle2, Trash2, X,
} from 'lucide-react';
import { api, PIPELINE_STAGES, SOURCES, VACANCY_STATUSES, stageLabel } from './recruitment/constants';
import { CandidateProfile } from './recruitment/CandidateProfile';
import { ScorecardForm } from './recruitment/ScorecardForm';
import { TrialForm } from './recruitment/TrialForm';
import { ReferenceForm } from './recruitment/ReferenceForm';
import { DecisionForm } from './recruitment/DecisionForm';
import { ExplorePipeline } from './recruitment/ExplorePipeline';

// ============================================================
// MAIN RECRUITMENT PAGE (router)
// ============================================================
export default function RecruitmentHub() {
  const [view, setView] = useState('dashboard');
  const [selectedId, setSelectedId] = useState(null);

  const navigate = (v, id = null) => { setView(v); setSelectedId(id); };

  // Deep-link: open a specific candidate when arriving from a notification link
  // e.g. /recruitment?candidate=<id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get('candidate');
    if (cid) {
      setView('candidate');
      setSelectedId(cid);
      window.history.replaceState({}, '', '/recruitment');
    }
  }, []);

  return (
    <div className="page-container" data-testid="recruitment-hub">
      {view === 'dashboard' && <Dashboard navigate={navigate} />}
      {view === 'vacancies' && <Vacancies navigate={navigate} />}
      {view === 'vacancy-edit' && <VacancyEdit id={selectedId} navigate={navigate} />}
      {view === 'pipeline' && <Pipeline navigate={navigate} />}
      {view === 'candidate' && <CandidateProfile id={selectedId} navigate={navigate} />}
      {view === 'candidate-add' && <CandidateAdd vacancyId={selectedId} navigate={navigate} />}
      {view === 'scorecard' && <ScorecardForm candidateId={selectedId} navigate={navigate} />}
      {view === 'trial' && <TrialForm candidateId={selectedId} navigate={navigate} />}
      {view === 'reference' && <ReferenceForm candidateId={selectedId} navigate={navigate} />}
      {view === 'decision' && <DecisionForm candidateId={selectedId} navigate={navigate} />}
      {view === 'explore' && <ExplorePipeline navigate={navigate} />}
    </div>
  );
}

// ============================================================
// DASHBOARD
// ============================================================
function Dashboard({ navigate }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { api('/api/recruitment/dashboard').then(setStats).catch(() => {}); }, []);

  if (!stats) return <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-muted rounded-2xl animate-skeleton" />)}</div>;

  const cards = [
    { label: 'Open Vacancies', value: stats.open_vacancies, icon: Briefcase, color: 'hsl(185, 55%, 38%)' },
    { label: 'New Applicants', value: stats.new_applicants, icon: UserPlus, color: 'hsl(142, 34%, 34%)' },
    { label: 'Awaiting Review', value: stats.awaiting_review, icon: Eye, color: 'hsl(38, 80%, 45%)' },
    { label: 'Interviews Booked', value: stats.interviews_booked, icon: Calendar, color: 'hsl(205, 70%, 40%)' },
    { label: 'Trials Pending', value: stats.trials_pending, icon: ClipboardCheck, color: 'hsl(270, 40%, 45%)' },
    { label: 'Refs Pending', value: stats.refs_pending, icon: FileCheck, color: 'hsl(14, 55%, 45%)' },
    { label: 'Final Decisions', value: stats.final_pending, icon: Award, color: 'hsl(350, 60%, 45%)' },
    { label: 'Hired This Month', value: stats.hired_this_month, icon: CheckCircle2, color: 'hsl(142, 34%, 34%)' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Recruitment Hub</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage hiring for Pure Souls</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {cards.map((c, i) => (
          <div key={i} className="ps-card-static p-4">
            <div className="flex items-center gap-2 mb-2"><c.icon size={18} style={{ color: c.color }} /><span className="text-xs text-muted-foreground">{c.label}</span></div>
            <p className="text-2xl font-bold">{c.value}</p>
          </div>
        ))}
      </div>
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Quick Actions</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <button onClick={() => navigate('vacancies')} className="ps-card p-4 flex items-center gap-3 text-left"><Briefcase size={20} className="text-primary" /><span className="text-sm font-semibold">Vacancies</span></button>
        <button onClick={() => navigate('pipeline')} className="ps-card p-4 flex items-center gap-3 text-left"><Users size={20} className="text-primary" /><span className="text-sm font-semibold">Candidate Pipeline</span></button>
        <button onClick={() => navigate('candidate-add')} className="ps-card p-4 flex items-center gap-3 text-left"><UserPlus size={20} className="text-primary" /><span className="text-sm font-semibold">Add Candidate</span></button>
      </div>
    </div>
  );
}

// ============================================================
// VACANCIES
// ============================================================
function Vacancies({ navigate }) {
  const [vacancies, setVacancies] = useState([]);
  useEffect(() => { api('/api/recruitment/vacancies').then(d => setVacancies(d.vacancies || [])).catch(() => {}); }, []);

  const statusColor = (s) => ({ Draft: 'badge-draft', Open: 'badge-active', Interviewing: 'badge-improving', Filled: 'badge-active', Closed: 'bg-muted text-muted-foreground' }[s] || 'badge-draft');

  return (
    <div>
      <button className="ch-back" onClick={() => navigate('dashboard')}><ArrowLeft size={16} /> Back</button>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold">Current Vacancies</h1>
        <button onClick={() => navigate('vacancy-edit')} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium" style={{ background: 'hsl(var(--primary))', color: 'white' }}><Plus size={16} /> New Vacancy</button>
      </div>
      <div className="space-y-3">
        {vacancies.map(v => (
          <div key={v.id} className="ps-card-static p-5 cursor-pointer" onClick={() => navigate('vacancy-edit', v.id)}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-bold">{v.role_title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{v.employment_type} · {v.hours} · {v.location}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColor(v.status)}`}>{v.status}</span>
            </div>
            {v.pathway && <p className="text-xs text-muted-foreground mt-2">{v.pathway}</p>}
          </div>
        ))}
        {vacancies.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No vacancies yet</p>}
      </div>
    </div>
  );
}

// ============================================================
// VACANCY EDIT/CREATE
// ============================================================
function VacancyEdit({ id, navigate }) {
  const [form, setForm] = useState({ role_title: '', employment_type: 'Casual', hours: '', location: 'Townsville, QLD', status: 'Draft', trial_period: '3 months', pathway: '', job_ad_text: '', internal_notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) api(`/api/recruitment/vacancies/${id}`).then(d => setForm(d)).catch(() => {});
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      if (id) await api(`/api/recruitment/vacancies/${id}`, { method: 'PATCH', body: JSON.stringify(form) });
      else await api('/api/recruitment/vacancies', { method: 'POST', body: JSON.stringify(form) });
      toast.success(id ? 'Vacancy updated' : 'Vacancy created');
      navigate('vacancies');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const F = (label, key, type = 'text') => (
    <div className="rec-field">
      <label className="rec-label">{label}</label>
      {type === 'textarea' ? <textarea value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="rec-input rec-textarea" /> :
        type === 'select' ? <select value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="rec-input">{(key === 'status' ? VACANCY_STATUSES : []).map(o => <option key={o}>{o}</option>)}</select> :
          <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="rec-input" />}
    </div>
  );

  return (
    <div>
      <button className="ch-back" onClick={() => navigate('vacancies')}><ArrowLeft size={16} /> Back</button>
      <h1 className="text-xl font-bold mb-6">{id ? 'Edit Vacancy' : 'New Vacancy'}</h1>
      <div className="ps-card-static p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {F('Role Title', 'role_title')}
          {F('Employment Type', 'employment_type')}
          {F('Hours', 'hours')}
          {F('Location', 'location')}
          {F('Status', 'status', 'select')}
          {F('Trial Period', 'trial_period')}
          {F('Start Date Target', 'start_date_target', 'date')}
          {F('Applications Closing', 'applications_closing_date', 'date')}
        </div>
        {F('Pathway', 'pathway', 'textarea')}
        {F('Job Ad Text', 'job_ad_text', 'textarea')}
        {F('Internal Notes', 'internal_notes', 'textarea')}
        <div className="flex gap-3 mt-6">
          <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}>{saving ? 'Saving...' : 'Save'}</button>
          <button onClick={() => navigate('vacancies')} className="px-6 py-2.5 rounded-xl text-sm font-medium border border-input">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CANDIDATE PIPELINE
// ============================================================
function Pipeline({ navigate }) {
  const [candidates, setCandidates] = useState([]);
  const [tab, setTab] = useState('active');
  const [confirmDel, setConfirmDel] = useState(null); // { id, name }
  const [confirmClear, setConfirmClear] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadCandidates = () => api('/api/recruitment/candidates').then(d => setCandidates(d.candidates || [])).catch(() => {});
  useEffect(() => { loadCandidates(); }, []);

  const active = candidates.filter(c => !['Hired', 'Rejected', 'Withdrawn'].includes(c.stage));
  const hired = candidates.filter(c => c.stage === 'Hired');
  const archived = candidates.filter(c => ['Rejected', 'Withdrawn'].includes(c.stage));

  const byStage = {};
  PIPELINE_STAGES.forEach(s => { byStage[s] = active.filter(c => c.stage === s); });

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api(`/api/recruitment/candidates/${confirmDel.id}`, { method: 'DELETE' });
      toast.success('Candidate deleted');
      setConfirmDel(null);
      await loadCandidates();
    } catch { toast.error('Failed to delete'); }
    setBusy(false);
  };

  const doClearArchived = async () => {
    setBusy(true);
    try {
      const res = await api('/api/recruitment/candidates/bulk-delete', { method: 'POST', body: JSON.stringify({ candidate_ids: archived.map(c => c.id) }) });
      toast.success(`Cleared ${res.deleted || 0} archived candidate${res.deleted === 1 ? '' : 's'}`);
      setConfirmClear(false);
      await loadCandidates();
    } catch { toast.error('Failed to clear'); }
    setBusy(false);
  };

  return (
    <div>
      <button className="ch-back" onClick={() => navigate('dashboard')}><ArrowLeft size={16} /> Back</button>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Candidate Pipeline</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('explore')} className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium border border-input hover:bg-muted"><Eye size={14} /> Explore Pipeline</button>
          <button onClick={() => navigate('candidate-add')} className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}><UserPlus size={14} /> Add</button>
        </div>
      </div>

      {/* Active/Hired/Archive tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-muted mb-5 w-fit">
        <button onClick={() => setTab('active')} className={`px-4 py-2 rounded-lg text-xs font-medium ${tab === 'active' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}>Active ({active.length})</button>
        <button onClick={() => setTab('hired')} className={`px-4 py-2 rounded-lg text-xs font-medium ${tab === 'hired' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}>Hired ({hired.length})</button>
        <button onClick={() => setTab('archive')} className={`px-4 py-2 rounded-lg text-xs font-medium ${tab === 'archive' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}>Archived ({archived.length})</button>
      </div>

      {tab === 'active' ? (
        /* Pipeline summary cards */
        <div className="space-y-2">
          {PIPELINE_STAGES.map(stage => {
            const cs = byStage[stage] || [];
            if (cs.length === 0) return (
              <div key={stage} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border/50 opacity-50">
                <span className="w-2 h-2 rounded-full bg-muted" />
                <span className="text-xs text-muted-foreground flex-1">{stageLabel(stage)}</span>
                <span className="text-xs text-muted-foreground">0</span>
              </div>
            );
            return (
              <div key={stage} className="ps-card-static overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'hsl(var(--primary))' }} />
                  <span className="text-xs font-semibold uppercase tracking-wider flex-1">{stageLabel(stage)}</span>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">{cs.length}</span>
                </div>
                <div className="divide-y divide-border/30">
                  {cs.map(c => (
                    <button key={c.id} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors" onClick={() => navigate('candidate', c.id)}>
                      <span className="text-xs text-muted-foreground font-mono">{c.candidate_number}</span>
                      <span className="text-sm font-medium flex-1">{c.candidate_name}</span>
                      <span className="text-xs text-muted-foreground">{c.source}</span>
                      <ChevronRight size={14} className="text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Hired or Archive list */
        <div className="space-y-2">
          {tab === 'archive' && archived.length > 0 && (
            <div className="flex justify-end mb-1">
              <button onClick={() => setConfirmClear(true)} data-testid="clear-archived-btn" className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"><Trash2 size={14} /> Clear all archived</button>
            </div>
          )}
          {(tab === 'hired' ? hired : archived).length === 0 && <p className="text-sm text-muted-foreground text-center py-8">{tab === 'hired' ? 'No hired candidates yet' : 'No archived candidates'}</p>}
          {(tab === 'hired' ? hired : archived).map(c => (
            <div key={c.id} className="ps-card-static p-4 flex items-center gap-3">
              <button className="flex items-center gap-3 flex-1 text-left" onClick={() => navigate('candidate', c.id)}>
                <span className="text-xs text-muted-foreground font-mono">{c.candidate_number}</span>
                <span className="text-sm font-medium flex-1">{c.candidate_name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${c.stage === 'Hired' ? 'badge-active' : c.stage === 'Rejected' ? 'badge-critical' : 'badge-draft'}`}>{c.stage}</span>
              </button>
              <button onClick={() => setConfirmDel({ id: c.id, name: c.candidate_name })} data-testid={`delete-row-${c.id}`} className="p-2 rounded-lg text-destructive hover:bg-destructive/10 transition-colors" title="Delete candidate"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      {/* Delete single confirmation */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-card rounded-2xl shadow-[var(--shadow-panel)] max-w-sm w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0"><Trash2 size={18} className="text-destructive" /></div>
              <h3 className="text-sm font-bold">Delete this candidate?</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-5">This permanently removes <strong>{confirmDel.name}</strong> and all their related records. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={doDelete} disabled={busy} data-testid="confirm-delete-row-btn" className="flex-1 px-5 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--destructive))', color: 'white' }}>{busy ? 'Deleting...' : 'Delete'}</button>
              <button onClick={() => setConfirmDel(null)} className="px-5 py-2.5 rounded-xl text-sm font-medium border border-input">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Clear all archived confirmation */}
      {confirmClear && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-card rounded-2xl shadow-[var(--shadow-panel)] max-w-sm w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0"><Trash2 size={18} className="text-destructive" /></div>
              <h3 className="text-sm font-bold">Clear all archived?</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-5">This permanently deletes all <strong>{archived.length}</strong> archived (rejected/withdrawn) candidates and their records. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={doClearArchived} disabled={busy} className="flex-1 px-5 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--destructive))', color: 'white' }}>{busy ? 'Clearing...' : 'Clear all'}</button>
              <button onClick={() => setConfirmClear(false)} className="px-5 py-2.5 rounded-xl text-sm font-medium border border-input">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// CANDIDATE ADD
// ============================================================
function CandidateAdd({ vacancyId, navigate }) {
  const [vacancies, setVacancies] = useState([]);
  const [form, setForm] = useState({ vacancy_id: vacancyId || '', candidate_name: '', phone: '', email: '', source: 'Other', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api('/api/recruitment/vacancies').then(d => { setVacancies(d.vacancies || []); if (!vacancyId && d.vacancies?.length) setForm(f => ({ ...f, vacancy_id: d.vacancies[0].id })); }).catch(() => {}); }, [vacancyId]);

  const save = async () => {
    if (!form.candidate_name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await api('/api/recruitment/candidates', { method: 'POST', body: JSON.stringify(form) });
      toast.success('Candidate added');
      navigate('pipeline');
    } catch { toast.error('Failed to add candidate'); }
    setSaving(false);
  };

  return (
    <div>
      <button className="ch-back" onClick={() => navigate('pipeline')}><ArrowLeft size={16} /> Back</button>
      <h1 className="text-xl font-bold mb-6">Add Candidate</h1>
      <div className="ps-card-static p-6 max-w-lg">
        <div className="space-y-4">
          <div className="rec-field"><label className="rec-label">Vacancy</label><select value={form.vacancy_id} onChange={e => setForm(f => ({ ...f, vacancy_id: e.target.value }))} className="rec-input">{vacancies.map(v => <option key={v.id} value={v.id}>{v.role_title}</option>)}</select></div>
          <div className="rec-field"><label className="rec-label">Name *</label><input value={form.candidate_name} onChange={e => setForm(f => ({ ...f, candidate_name: e.target.value }))} className="rec-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rec-field"><label className="rec-label">Phone</label><input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="rec-input" /></div>
            <div className="rec-field"><label className="rec-label">Email</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="rec-input" /></div>
          </div>
          <div className="rec-field"><label className="rec-label">Source</label><select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} className="rec-input">{SOURCES.map(s => <option key={s}>{s}</option>)}</select></div>
          <div className="rec-field"><label className="rec-label">Notes</label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="rec-input rec-textarea" /></div>
        </div>
        <button onClick={save} disabled={saving} className="mt-6 px-6 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'hsl(var(--primary))', color: 'white' }}>{saving ? 'Adding...' : 'Add Candidate'}</button>
      </div>
    </div>
  );
}
